import { Component, OnInit } from '@angular/core';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { HourglassService } from 'src/app/services/hourglass.service';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';

@Component({
  selector: 'app-tab-teams',
  templateUrl: './teams.component.html',
  styleUrls: ['./teams.component.css']
})
export class TeamsComponent implements OnInit {
  token!: string;
  userID!: number;

  loading = false;
  errorMessage = '';

  listTeams: Array<{ id: number; team_name: string; active: number }> = [];
  allUsers: Array<{ id: number; fullName: string }> = [];
  teamMembers: Array<{ team_id: number; user_id: number }> = [];

  private membersByTeam = new Map<number, number[]>();
  private userNameById  = new Map<number, string>();

  editorTeamName = '';
  teamForm: { teamID: number | null; memberIds: number[] } = {
    teamID: null,
    memberIds: []
  };

  gridApi!: GridApi;
  rowData: Array<{ teamId: number; name: string; membersText: string; activeText: string; activeSort: number }> = [];

  columnDefs: ColDef[] = [
    {
      headerName: '',
      colId: 'actions',
      width: 46,
      minWidth: 46,
      maxWidth: 46,
      pinned: 'right',
      suppressMenu: true,
      sortable: false,
      resizable: false,
      cellRenderer: () =>
        `<button class="btn btn-link p-0 text-danger teams-row-delete" title="Delete">
           <i class="fas fa-fw fa-trash c_bluedk"></i>
         </button>`,
      onCellClicked: (p) => this.requestDelete(p.data?.teamId)
    },

    {
      headerName: 'Team Name',
      field: 'name',
      flex: 1,
      sortable: true,
      resizable: true
    },
    {
      headerName: 'Members',
      field: 'membersText',
      flex: 2,
      sortable: true,
      resizable: true,
      tooltipField: 'membersText'
    },
    {
      headerName: 'Active',
      field: 'activeText',
      width: 100,
      sortable: true,
      valueGetter: p => p.data?.activeText,
      comparator: (a: string, b: string, nodeA, nodeB) => {
        const aKey = nodeA?.data?.activeSort ?? 0;
        const bKey = nodeB?.data?.activeSort ?? 0;
        if (aKey !== bKey) return bKey - aKey;
        const nameA = (nodeA?.data?.name || '').toLowerCase();
        const nameB = (nodeB?.data?.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true
  };

  constructor(private hourglassService: HourglassService) {}

  ngOnInit(): void {
    const userStr = sessionStorage.getItem('wOSUser') || '{}';
    const prefsStr = sessionStorage.getItem('wOSUserPrefs') || '{}';
    const parsedUser = JSON.parse(userStr);
    const parsedPrefs = JSON.parse(prefsStr);

    this.token = parsedUser?.stsTokenManager?.accessToken || '';
    this.userID = Number(parsedPrefs?.id) || 0;

    this.getUserGroupData();
  }

  getUserGroupData(): void {
    this.loading = true;
    this.errorMessage = '';

    this.hourglassService.getUserGroups(this.token)
      .then((res: any) => {
        const teams   = Array.isArray(res?.teams)   ? res.teams   : [];
        const members = Array.isArray(res?.members) ? res.members : (res?.teamMembers || []);
        const users   = Array.isArray(res?.users)   ? res.users   : [];

        this.listTeams = teams.map((t: any) => ({
          id: Number(t.id),
          team_name: t.team_name ?? t.name ?? '',
          active: Number(t.active ?? 1)
        }));

        this.allUsers = users.map((u: any) => {
          const full = u.full_name ?? `${u.f_name ?? ''} ${u.l_name ?? ''}`.trim();
          this.userNameById.set(Number(u.id), full);
          return { id: Number(u.id), fullName: full };
        });

        this.membersByTeam.clear();
        for (const m of members) {
          const t = Number(m.team_id);
          const u = Number(m.user_id);
          if (!this.membersByTeam.has(t)) this.membersByTeam.set(t, []);
          this.membersByTeam.get(t)!.push(u);
        }

        this.buildGridRows();
      })
      .catch(err => {
        console.error('getUserGroups failed', err);
        this.errorMessage = 'Failed to load teams.';
      })
      .finally(() => (this.loading = false));
  }

  private buildGridRows(): void {
    const rows = this.listTeams.map(t => {
      const memberIds = this.membersByTeam.get(t.id) ?? [];
      const names = memberIds
        .map(id => this.userNameById.get(id) || '')
        .filter(s => !!s)
        .join(', ');

      return {
        teamId: t.id,
        name: t.team_name,
        membersText: names,
        activeText: t.active ? 'Yes' : 'No',
        activeSort: t.active ? 1 : 0
      };
    });

    rows.sort((a, b) => (b.activeSort - a.activeSort) || a.name.localeCompare(b.name));
    this.rowData = rows;
  }

  onGridReady(e: GridReadyEvent): void {
    this.gridApi = e.api;
  }

  onFirstDataRendered(): void {
    this.gridApi.sizeColumnsToFit();
  }

  onRowClicked(e: any): void {
    const tgt = e?.event?.target as HTMLElement | null;
    if (tgt && tgt.closest('.teams-row-delete')) {
      return; 
    }
    const data = e?.data;
    if (!data) { return; }
    const team = this.listTeams.find(t => t.id === data.teamId);
    this.teamForm.teamID = data.teamId;
    this.editorTeamName = team?.team_name ?? '';
    this.teamForm.memberIds = [...(this.membersByTeam.get(data.teamId) ?? [])];
    this.toggleModal('show');
  }

  openCreateTeam(): void {
    this.teamForm.teamID = null;
    this.editorTeamName = '';
    this.teamForm.memberIds = [];
    this.toggleModal('show');
  }

  onMemberToggle(userId: number, ev: Event): void {
    const checked = (ev.target as HTMLInputElement).checked;
    const arr = this.teamForm.memberIds;
    if (checked && !arr.includes(userId)) {
      arr.push(userId);
    } else if (!checked) {
      const ix = arr.indexOf(userId);
      if (ix >= 0) arr.splice(ix, 1);
    }
  }

  selectAllMembers(): void {
    this.teamForm.memberIds = this.allUsers.map(u => u.id);
  }

  clearAllMembers(): void {
    this.teamForm.memberIds = [];
  }

  toggleModal(mode: 'show' | 'hide'): void {
    ($('#teamEditorModal') as any).modal(mode);
  }

  closeEditorModal(): void {
    this.toggleModal('hide');
  }

  saveTeam(): void {
    const isCreate = !this.teamForm.teamID;
    const name = (this.editorTeamName || '').trim();
    if (!name) {
      alert('Team name is required.');
      return;
    }

    const payload = {
      mode: isCreate ? 'create' : 'update',
      team: {
        id: isCreate ? null : Number(this.teamForm.teamID),
        name,
        active: 1
      },
      memberIds: this.teamForm.memberIds.map(Number),
      modifiedBy: this.userID
    };

    this.hourglassService.saveUserGroup(payload.mode, payload, this.token)
      .then(() => {
        this.getUserGroupData();
        this.toggleModal('hide');
      })
      .catch(err => {
        console.error('saveUserGroup failed', err);
        alert('Save failed.');
      });
  }

  // === NEW: delete handler ===
  private requestDelete(teamId: number | undefined): void {
    if (!teamId) { return; }
    const team = this.listTeams.find(t => t.id === teamId);
    const name = team?.team_name ?? 'this team';

    if (!confirm(`Delete "${name}"?\n\nThis will remove the team and all of its member links.`)) {
      return;
    }

    const payload = {
      mode: 'delete',
      team: { id: Number(teamId) },
      memberIds: [],
      modifiedBy: this.userID
    };

    this.hourglassService.saveUserGroup('delete', payload, this.token)
      .then(() => this.getUserGroupData())
      .catch(err => {
        console.error('delete team failed', err);
        alert('Delete failed.');
      });
  }
}
